import {
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";

import {
  computeActionEligibility,
  formatFieldValue,
  getDiscrepancyStatus,
  getStatusLabel,
  type ActionKey,
} from "./DiscrepancyReviewDialog.helpers";
import { ResolveApplyToOthers } from "./ResolveApplyToOthers";
import {
  type MatchingDiscrepancyRow,
  type ResolveActionOption,
} from "./ResolveDiscrepanciesDialog";
import { E_MANIFEST_HELPER, Q1Options, SaveWarnings } from "./ResolveQ1Options";
import {
  inactiveResultText,
  sharedRowValue,
  type SourceWriteState,
  type ResolveRow,
} from "./resolveWithValue.helpers";
import { ResultSection } from "./ResultSection";
import { RESOLVE_DISCREPANCIES, useResolveWithValueForm } from "./useResolveWithValueForm";
import { formatVendorLineAddition, UNPAIRED_WASTE_LINE_FIELD } from "./vendorLineAddition";

export { RESOLVE_DISCREPANCIES };

export interface ResolveWithValueDialogProps {
  open: boolean;
  rows: ResolveRow[];
  clearSelection: () => void;
  /** The single targeted row's id — drives "apply to others". Only meaningful when rows.length === 1. */
  singleDiscrepancyId?: string;
  isStaff: boolean;
  /** "Non-Discrepancy", pre-computed by the parent against these same rows. Always offered. */
  dismissOption: ResolveActionOption;
  /** "Can't Resolve — Mark as Blocked", pre-computed by the parent. Undefined hides the option (customer, or a multi-manifest review). */
  cantDetermineOption?: ResolveActionOption;
  /** Other in-flight row mutations (reopen, undo, …) from the parent dialog — Save stays disabled while one runs. */
  otherMutationLoading: boolean;
  onClose: () => void;
  /** Reused for the answers that don't call resolveDiscrepancies: dismiss and "can't determine" (routes to the Mark Blocked dialog). */
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  notifyMutated: () => void;
  /** Opens the row's source document in the review dialog's preview pane. Closes this dialog first, since the pane sits behind it. */
  onViewDocument?: (documentId: string, fileExtension: string | null | undefined) => void;
}

// Vendor data and the manifest document take the picked value whenever they
// differ. e-Manifest is also a target when it differs, and the row waits in
// Fix pending until RCRAInfo is corrected.
const NO_CHANGE_SX = { color: "text.secondary" } as const;

/** "No change (reason)" for a source that keeps its value. */
function NoChange({ reason }: { reason?: string }) {
  return (
    <Typography variant="body2" sx={NO_CHANGE_SX}>
      No change{reason ? ` (${reason})` : ""}
    </Typography>
  );
}

/** One table row: the source, its value now, and its value after save. */
function SourceRow({
  field,
  label,
  currentValue,
  newValue,
  state,
  updateText,
}: {
  field: string;
  label: string;
  currentValue: string | null;
  newValue: string | null;
  state: SourceWriteState;
  /** Replaces the formatted new value when the write is not a value, e.g. adding a waste line. */
  updateText?: string;
}) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
      <TableCell>{formatFieldValue(field, currentValue)}</TableCell>
      <TableCell>
        {state.writes ? (
          <strong>{updateText ?? formatNewValue(field, newValue)}</strong>
        ) : (
          <NoChange reason={state.reason} />
        )}
      </TableCell>
    </TableRow>
  );
}

function formatNewValue(field: string, newValue: string | null): string {
  return newValue == null ? "The picked value" : formatFieldValue(field, newValue);
}

/** e-Manifest's row: we can't write it, so the reviewer corrects it in RCRAInfo and the row waits in Fix pending. */
function EManifestRow({
  field,
  currentValue,
  newValue,
  state,
}: {
  field: string;
  currentValue: string | null;
  newValue: string | null;
  state: SourceWriteState;
}) {
  if (!state.writes) {
    return (
      <SourceRow
        field={field}
        label="e-Manifest"
        currentValue={currentValue}
        newValue={newValue}
        state={state}
      />
    );
  }
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>e-Manifest</TableCell>
      <TableCell>{formatFieldValue(field, currentValue)}</TableCell>
      <TableCell>
        <strong>{formatNewValue(field, newValue)}</strong>
        <Typography variant="caption" color="text.secondary" display="block">
          {E_MANIFEST_HELPER}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/** The document-value context bar at the top, with an optional link into the row's source document. */
function DocumentBar({
  field,
  label,
  value,
  showOpenLink,
  onOpen,
}: {
  field: string;
  label: string;
  value: string | null | undefined;
  showOpenLink: boolean;
  onOpen: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ px: 1.5, py: 1, bgcolor: "action.hover", borderRadius: 1 }}
    >
      <Typography variant="body2">
        {label}: <strong>{formatFieldValue(field, value)}</strong>
      </Typography>
      {showOpenLink && (
        <Link component="button" type="button" variant="body2" onClick={onOpen}>
          View document
        </Link>
      )}
    </Stack>
  );
}

/** The status rows are in now, or "Varies by row". */
function currentStatusLabel(rows: readonly ResolveRow[]): string {
  const statuses = new Set(rows.map((r) => getDiscrepancyStatus(r)));
  const [only] = statuses;
  if (statuses.size !== 1 || only == null) return "Varies by row";
  return getStatusLabel(only) ?? "—";
}

/** The status now and the status save will leave, under the Preview table. */
function StatusLine({
  rows,
  statusAfterSave,
}: {
  rows: readonly ResolveRow[];
  statusAfterSave: string;
}) {
  return (
    <Typography variant="body2" sx={{ mt: 1 }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        Status:
      </Box>{" "}
      {currentStatusLabel(rows)} → <strong>{statusAfterSave}</strong>
    </Typography>
  );
}

/** The Preview before a value answer, or for dismiss / block: just the reason. */
function InactivePreviewText({ text }: { text: string }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  );
}

/** The field every targeted row shares, or "" when they span several (values then render unformatted). */
function sharedField(rows: readonly ResolveRow[]): string {
  const fields = new Set(rows.map((r) => r.field));
  return fields.size === 1 ? rows[0].field : "";
}

interface PreviewTargetsProps {
  rows: ResolveRow[];
  /** What the targeted rows become, or null when they'd get different values. */
  newValue: string | null;
  /** The status save leaves, e.g. "Fix pending" or "1 Resolved · 1 Fix pending". */
  statusAfterSave: string;
  vendorState: SourceWriteState;
  eManifestState: SourceWriteState;
  documentState: SourceWriteState;
  /** Copies left disagreeing with the answer; shown, never blocking. */
  saveWarnings: string[];
}

/** Question 2: each source, and what it becomes on save. */
function PreviewTargets({
  rows,
  newValue,
  statusAfterSave,
  vendorState,
  eManifestState,
  documentState,
  saveWarnings,
}: PreviewTargetsProps) {
  const field = sharedField(rows);
  return (
    <Box>
      <Table size="small" sx={{ "& td, & th": { px: 1, verticalAlign: "top" } }}>
        <TableHead>
          <TableRow>
            <TableCell>Source</TableCell>
            <TableCell>Current</TableCell>
            <TableCell>Update</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <SourceRow
            field={field}
            label="Manifest document"
            currentValue={sharedRowValue(rows, (r) => r.manifestDocumentValue)}
            newValue={newValue}
            state={documentState}
          />
          <EManifestRow
            field={field}
            currentValue={sharedRowValue(rows, (r) => r.eManifestValue)}
            newValue={newValue}
            state={eManifestState}
          />
          <SourceRow
            field={field}
            label="Vendor"
            currentValue={sharedRowValue(rows, (r) => r.vendorValue)}
            newValue={newValue}
            state={vendorState}
            updateText={
              field === UNPAIRED_WASTE_LINE_FIELD ? formatVendorLineAddition(newValue) : undefined
            }
          />
        </TableBody>
      </Table>
      <StatusLine rows={rows} statusAfterSave={statusAfterSave} />
      <SaveWarnings warnings={saveWarnings} />
    </Box>
  );
}

/** Mirrors computeActionEligibility's signature against the matching rows, for the hook's "apply to others" eligibility. */
function computeActionEligibleIds(
  action: "nonDiscrepancy" | "cantDetermine",
  matchingRows: readonly MatchingDiscrepancyRow[]
): string[] {
  return computeActionEligibility(action, matchingRows).eligibleIds;
}

/**
 * The resolve flow for a Data Source Conflict: pick the correct value, then
 * review a collapsed Preview of what Save writes. Staff-only; mutation and
 * form state live in useResolveWithValueForm.
 */
export function ResolveWithValueDialog(props: ResolveWithValueDialogProps) {
  const { open, rows, cantDetermineOption, onClose } = props;
  const form = useResolveWithValueForm({ ...props, computeActionEligibleIds });

  if (form.totalCount === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (form.loading) return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      title={form.title}
      primaryActionButtonProps={{
        onClick: () => form.handleSave(),
        loading: form.resolving,
        disabled: form.saveDisabled,
      }}
      cancelButtonProps={{ disabled: form.loading }}
    >
      {form.documentBarLabel && (
        <DocumentBar
          field={form.singleRow?.field ?? ""}
          label={form.documentBarLabel.split(":")[0]}
          value={form.singleRow?.manifestDocumentValue}
          showOpenLink={form.showOpenDocumentLink}
          onOpen={form.openDocument}
        />
      )}

      <Q1Options
        rows={rows}
        singleRow={form.singleRow}
        answer={form.answer}
        loading={form.loading}
        otherValueDisabledReason={form.otherValueDisabledReason}
        documentDisabledReason={form.documentDisabledReason}
        valueAnswersDisabledReason={form.valueAnswersDisabledReason}
        dismissDisabledReason={form.dismissDisabledReason}
        cantDetermineOption={cantDetermineOption}
        manualText={form.manualText}
        manualCodes={form.manualCodes}
        box18aAmount={form.box18aAmount}
        onBox18aAmountChange={form.setBox18aAmount}
        onSelectAnswer={form.selectAnswer}
        onTextChange={form.setManualText}
        onCodesChange={form.setManualCodes}
      />

      <ResultSection
        footer={
          form.showsPreview &&
          form.saveDisabledReason && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {form.saveDisabledReason}
            </Typography>
          )
        }
      >
        {form.showsPreview && form.vendorState && form.eManifestState && form.documentState ? (
          <PreviewTargets
            rows={rows}
            newValue={form.newValue}
            statusAfterSave={form.statusAfterSave}
            vendorState={form.vendorState}
            eManifestState={form.eManifestState}
            documentState={form.documentState}
            saveWarnings={form.saveWarnings}
          />
        ) : (
          <InactivePreviewText text={inactiveResultText(form.answer)} />
        )}
      </ResultSection>

      {form.showApplyToOthers && form.answer && (
        <ResolveApplyToOthers
          matchingRows={form.matchingRows}
          otherEligible={form.otherEligibleRows}
          actionPicked
          checked={form.applyToOthers}
          disabled={form.loading}
          onChange={form.setApplyToOthers}
        />
      )}

      <TextField
        label="Note (optional)"
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
        size="small"
        value={form.note}
        onChange={(e) => form.setNote(e.target.value)}
      />
    </Dialog>
  );
}
